// BEGIN FILE: src/utils/familyLifeAreaScores.ts
/**
 * Calculate life area scores for family members based on their birth charts
 * Reuses life area definitions and house score calculation patterns
 */

import { FAMILY_LIFE_AREAS } from '../constants/familyHouseMapping';

// Life area to house mapping (standardized across app)
// Must match LifeAreaRemediesTable.tsx and LifeAreasDashboard.tsx
const LIFE_AREA_HOUSES: Record<string, number[]> = {
  Health: [1, 6, 8, 12],
  Wealth: [1, 2, 5, 9, 11, 12],
  Job: [1, 6, 10],
  Business: [1, 3, 7, 11],
  Marriage: [1, 7],
  Children: [1, 5],
};

export interface LifeAreaScore {
  lifeArea: string;
  score: number;
  color: string;
  displayText: string;
}

/**
 * Get color based on score value
 * Maps score ranges to color codes (green = good, red = challenging)
 */
export function getScoreColor(score: number): string {
  if (score >= 15) return '#15803d'; // green-700 (excellent)
  if (score >= 8) return '#16a34a';  // green-600 (good)
  if (score >= 0) return '#84cc16';  // lime-500 (neutral-positive)
  if (score >= -8) return '#f97316'; // orange-500 (challenging)
  if (score >= -15) return '#dc2626'; // red-600 (difficult)
  return '#b91c1c'; // red-700 (severe)
}

/**
 * Calculate life area score as cumulative average of house scores
 * Handles both positive and negative scores
 */
function calculateLifeAreaScore(houses: number[], houseScores: Record<number, number>): number {
  const values = houses.map(h => houseScores[h] ?? 0);
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / houses.length;
}

/**
 * Format score for display (e.g., "+12.5", "-3.2", "0.0")
 */
function formatScore(score: number): string {
  const sign = score > 0 ? '+' : '';
  return `${sign}${score.toFixed(1)}`;
}

/**
 * Calculate all life area scores for a family member
 * 
 * @param houseScores - Record of house scores (1-12) from birth chart
 * @returns Array of life area scores with colors and display text
 */
export function calculateFamilyMemberLifeAreas(
  houseScores: Record<number, number> | null
): LifeAreaScore[] {
  if (!houseScores) {
    // Return default scores if no data
    return FAMILY_LIFE_AREAS.map(lifeArea => ({
      lifeArea,
      score: 0,
      color: '#9ca3af', // neutral gray
      displayText: '—'
    }));
  }

  // Validate that we have all 12 houses
  const hasAllHouses = Array.from({ length: 12 }, (_, i) => i + 1)
    .every(h => typeof houseScores[h] === 'number' && Number.isFinite(houseScores[h]));

  if (!hasAllHouses) {
    console.warn('[familyLifeAreaScores] Invalid house scores - missing or non-numeric values');
    return FAMILY_LIFE_AREAS.map(lifeArea => ({
      lifeArea,
      score: 0,
      color: '#9ca3af',
      displayText: '—'
    }));
  }

  // Calculate scores for each life area
  return FAMILY_LIFE_AREAS.map(lifeArea => {
    const houses = LIFE_AREA_HOUSES[lifeArea] || [1]; // Default to H1 if not defined
    const score = calculateLifeAreaScore(houses, houseScores);
    const color = getScoreColor(score);
    const displayText = formatScore(score);

    return {
      lifeArea,
      score,
      color,
      displayText
    };
  });
}

/**
 * Get a simplified color category for chip display
 * Maps to Joy UI color variants
 */
export function getChipColor(score: number): 'success' | 'warning' | 'danger' | 'neutral' {
  if (score >= 8) return 'success';
  if (score >= 0) return 'neutral';
  if (score >= -8) return 'warning';
  return 'danger';
}
